// @flow
import type { PerformanceResourceTimingV2 } from 'app/packages/pinterest-flow-types/performanceTiming';
import { DEBUG_TRACING } from 'app/packages/trace-logger/debugFlags';

let customBuffer: Array<PerformanceResourceTimingV2> | void;

function clearDefaultBuffer() {
  if (window.performance && window.performance.clearResourceTimings) {
    window.performance.mark('resourceBufferCleared');
    if (DEBUG_TRACING) {
      // eslint-disable-next-line no-console
      console.info('Warning: Clearing resource timings.');
    }
    window.performance.clearResourceTimings();
  }
}

// NOTE: average size of an entry is about 0.5 KB
export const setCachingStrategy = ({ size }: {| size: number |}) => {
  if (__SERVER__ || !window.performance) {
    return;
  }
  if (window.PerformanceObserver) {
    customBuffer = window.performance.getEntriesByType('resource');
    const observer = new window.PerformanceObserver((list) => {
      customBuffer = (customBuffer || []).concat(list.getEntries()).slice(-size);
    });
    observer.observe({ entryTypes: ['resource'] });
    window.addEventListener('beforeunload', () => {
      observer.disconnect();
    });
  } else {
    window.addEventListener('popstate', () => {
      // IE fallback: Check every time there is a popstate whether we're reaching max resource buffer
      const bufferLength = window.performance.getEntries().length;
      if (bufferLength > size) {
        clearDefaultBuffer();
      }
    });
    // Increase the limit of the Resource Timing Buffer from 150 to 1000 because we're a very resource-heavy SPA
    // (average size of an entry is about 500 bytes, making this a max of 500KB)
    if (window.performance.setResourceTimingBufferSize) {
      window.performance.setResourceTimingBufferSize(size);
    }
  }
  // Add a listener for when the resource buffer is full so we can clear it
  // (supported in Chrome, FF, and Safari but not IE)
  window.performance.onresourcetimingbufferfull = () => {
    window.performance.mark('resourceBufferFull');
    if (DEBUG_TRACING) {
      // eslint-disable-next-line no-console
      console.info('Warning: Resource Timing Buffer is full.');
    }
    if (!customBuffer) {
      clearDefaultBuffer();
    }
  };
};

export const getEntries = (): Array<PerformanceResourceTimingV2> =>
  customBuffer || window.performance.getEntriesByType('resource');

export const handleRouteChange = () => {
  if (customBuffer) {
    customBuffer = [];
  }
};
